import { computed, ref } from 'vue'
import { api } from '@/global/services/api'
import { isFunction } from '@/global/utils/utils'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'
import { useActiveStatus } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useActiveStatus'
import {
  toProviderForm,
  toProviderStatus
} from '@/modules/road-maintenance-module/winter-service-v2/common/composables/providerData'
import { useOperatorFields } from '@/modules/road-maintenance-module/winter-service-v2/subcontractors/composables/useOperatorFields'
import { ACTIVE_STATUS, WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  buildOperatorsEndpoint,
  OPERATORS_TABLE_KEY,
  SUBCONTRACTORS_TRANSLATION_NAMESPACE as NS
} from '@/modules/road-maintenance-module/winter-service-v2/subcontractors/config/constants'

const FORM_COLUMNS_COUNT = 3

const VIEW = {
  list: 'list',
  form: 'form'
}

/**
 * The operators (izvršitelji) tab: the listing of the subcontractor's operators and,
 * in its place, the one form that both shows an operator and creates a new one - the
 * screen is the same, only the buttons above it differ.
 *
 * The tab drives its own requests instead of leaning on the tab layout's save/fetch:
 * that one is bound to a single record per tab, while this tab moves between a list
 * and whichever operator was picked from it.
 */
export function useOperators (options) {
  const {
    translate,
    subcontractorId,
    openGeolocationDialogFn,
    openInfoDialogFn,
    closeInfoDialogFn,
    setInfoDialogLoaderFn
  } = options || {}

  const { buildConfig } = useTableService()
  const { statusCellConfig, buildStatusCell } = useActiveStatus({ trans: translate })
  const { fields } = useOperatorFields({
    translate,
    openGeolocationDialogFn
  })

  const endpoint = computed(() => buildOperatorsEndpoint(subcontractorId))

  const view = ref(VIEW.list)
  const isEditing = ref(false)
  const isCreating = ref(false)
  const isSaving = ref(false)
  const openedOperatorId = ref(null)
  const formData = ref({})
  const originalFormData = ref({})
  const validationErrors = ref({})

  const isListVisible = computed(() => view.value === VIEW.list)

  const setFormData = (data) => {
    formData.value = { ...(data || {}) }
  }

  // Always a new object, never a mutation: a key the form did not start with - the
  // address the map picker writes back, for one - would otherwise never reach the field.
  const updateFormData = (data) => {
    formData.value = {
      ...formData.value,
      ...(data || {})
    }
  }

  const updateValidationErrors = (errors) => {
    validationErrors.value = {
      ...validationErrors.value,
      ...(errors || {})
    }
  }

  const clearValidationError = (key) => {
    if (!key) {
      return
    }

    const { [key]: removed, ...rest } = validationErrors.value
    validationErrors.value = rest
  }

  const removeDataPropertyByKey = (key) => {
    const { [key]: removed, ...rest } = formData.value
    formData.value = rest
  }

  const replacePropertyValueByKey = (key, value) => {
    updateFormData({ [key]: value })
  }

  const refreshTableData = () => {
    const { fetchData } = registeredTableHandlers?.[OPERATORS_TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }

    fetchData()
  }

  const backToList = () => {
    view.value = VIEW.list
    isEditing.value = false
    isCreating.value = false
    openedOperatorId.value = null
    validationErrors.value = {}
    setFormData({})
  }

  const openOperator = (event) => {
    const operator = event?.data || {}
    if (!operator?.id) {
      return
    }

    openedOperatorId.value = operator.id
    validationErrors.value = {}
    // The listing reports the whole operator, so the form it opens needs no request
    // of its own - only the same nested-to-flat translation the details tab does.
    const form = {
      ...operator,
      ...toProviderForm(operator)
    }
    setFormData(form)
    originalFormData.value = { ...form }
    isCreating.value = false
    view.value = VIEW.form
  }

  const startCreating = () => {
    openedOperatorId.value = null
    validationErrors.value = {}
    setFormData({})
    originalFormData.value = {}
    isCreating.value = true
    isEditing.value = true
    view.value = VIEW.form
  }

  const setEdit = (value) => {
    isEditing.value = !!value
  }

  const cancelForm = () => {
    if (isCreating.value) {
      backToList()
      return
    }

    setFormData(originalFormData.value)
    validationErrors.value = {}
    isEditing.value = false
  }

  /**
   * The payload the operator endpoints expect: flat, with the status back as the
   * `is_active` flag. The subcontractor is never part of it - it comes from the URL.
   */
  const buildPayload = () => {
    const {
      name,
      oib,
      address,
      latitude,
      longitude,
      status,
      responsible_person_name: responsiblePersonName,
      responsible_person_email: responsiblePersonEmail,
      responsible_person_phone: responsiblePersonPhone
    } = formData.value || {}

    const payload = {
      name: name ?? null,
      oib: oib ?? null,
      address: address ?? null,
      responsible_person_name: responsiblePersonName ?? null,
      responsible_person_email: responsiblePersonEmail ?? null,
      responsible_person_phone: responsiblePersonPhone ?? null
    }

    // Coordinates are only ever set through the address picker. Sending them empty
    // would fail validation, and leaving them out keeps the geolocation as it is.
    if (latitude && longitude) {
      payload.latitude = latitude
      payload.longitude = longitude
    }

    // A new operator starts off active - the status can only be edited once it exists.
    if (!isCreating.value) {
      payload.is_active = status !== ACTIVE_STATUS.inactive
    }

    return payload
  }

  /**
   * The coordinates have no field of their own - they are picked together with the
   * address - so a complaint about them has to be shown on the address field.
   */
  const mapValidationErrors = (errors) => {
    const { latitude, longitude, ...rest } = errors || {}
    const addressError = rest.address || latitude || longitude

    return addressError
      ? { ...rest, address: addressError }
      : rest
  }

  const saveOperator = async () => {
    try {
      isSaving.value = true
      validationErrors.value = {}
      const payload = buildPayload()

      if (isCreating.value) {
        await api(WINTER_SERVICE_MODULE).post(endpoint.value, payload)
        backToList()
        return
      }

      const { data } = await api(WINTER_SERVICE_MODULE).patch(
        `${endpoint.value}/${openedOperatorId.value}`,
        payload
      )

      const form = {
        ...data,
        ...toProviderForm(data)
      }
      setFormData(form)
      originalFormData.value = { ...form }
      isEditing.value = false
    }
    catch (exception) {
      const { errors } = exception?.response?.data || {}
      validationErrors.value = mapValidationErrors(errors)
    }
    finally {
      isSaving.value = false
    }
  }

  const deleteOperator = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      setInfoDialogLoaderFn?.(true)
      await api(WINTER_SERVICE_MODULE).delete(`${endpoint.value}/${id}`)
      closeInfoDialogFn?.()
      refreshTableData()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn?.(false)
    }
  }

  const onOperatorDelete = (event) => {
    if (!isFunction(openInfoDialogFn)) {
      return
    }

    openInfoDialogFn({
      title: translate(`${NS}.operator_delete_confirmation_title`),
      message: translate(`${NS}.operator_delete_confirmation_message`),
      onConfirm: () => deleteOperator(event)
    })
  }

  // The listing reports an operator nested, the same way the subcontractor is - the
  // columns read their value off `geolocation` and `responsible_person`.
  const cellsConfig = computed(() => ({
    address: {
      custom_display_value: (data) => data?.geolocation?.address || ''
    },
    responsible_person_name: {
      custom_display_value: (data) => data?.responsible_person?.name || ''
    },
    responsible_person_email: {
      custom_display_value: (data) => data?.responsible_person?.email || ''
    },
    responsible_person_phone: {
      custom_display_value: (data) => data?.responsible_person?.phone || ''
    },
    status: {
      ...statusCellConfig,
      custom_display_value: (data) => buildStatusCell(toProviderStatus(data))
    }
  }))

  const headers = computed(() => ([
    {
      label: translate(`${NS}.id`),
      key: 'id',
      always_visible: true
    },
    {
      label: translate(`${NS}.operator_name`),
      key: 'name',
      always_visible: true
    },
    {
      label: translate(`${NS}.oib`),
      key: 'oib',
      always_visible: true
    },
    {
      label: translate(`${NS}.address`),
      key: 'address',
      always_visible: true
    },
    {
      label: translate(`${NS}.status`),
      key: 'status',
      always_visible: true
    },
    {
      label: translate(`${NS}.responsible_person`),
      key: 'responsible_person_name',
      always_visible: true
    },
    {
      label: translate(`${NS}.responsible_person_email`),
      key: 'responsible_person_email',
      always_visible: true
    },
    {
      label: translate(`${NS}.responsible_person_phone`),
      key: 'responsible_person_phone',
      always_visible: true
    }
  ]))

  // Only the filters the operators listing understands.
  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'name',
        label: translate(`${NS}.operator_name`)
      },
      {
        key: 'oib',
        label: translate(`${NS}.oib`)
      }
    ]
  }))

  const actionsConfig = computed(() => ([
    showDetailsAction(translate, (event) => openOperator(event)),
    deleteAction(translate, onOperatorDelete)
  ]))

  const tableConfig = computed(() => buildConfig({
    key: OPERATORS_TABLE_KEY,
    // The table lives inside a tab, not on a screen of its own - it fills the tab
    // and scrolls its rows internally.
    fillHeight: true,
    apiConfig: {
      get: {
        module: WINTER_SERVICE_MODULE,
        route: endpoint.value
      }
    },
    rowDblClick: (event) => openOperator(event),
    title_and_back_config: {
      title: translate(`${NS}.operators`)
    },
    search_and_buttons_config: {
      showNewBtn: true,
      newClick: startCreating,
      newButtonConfig: {
        label: translate(`${NS}.new_operator`)
      }
    },
    tableConfig: {
      cellsConfig: cellsConfig.value,
      headers: headers.value,
      actionsConfig: actionsConfig.value,
      filtersConfig: filtersConfig.value
    }
  }))

  const formActionsConfig = computed(() => ({
    title: isCreating.value
      ? translate(`${NS}.new_operator`)
      : translate(`${NS}.operator_details`),
    showEditButtons: true,
    showBackButton: {
      onGoBack: backToList
    },
    disableSave: isSaving.value,
    visible: true
  }))

  const fieldsRendererConfig = computed(() => ({
    columnsCount: FORM_COLUMNS_COUNT,
    fields: fields.value
  }))

  return {
    isListVisible,
    isEditing,
    isCreating,
    formData,
    validationErrors,
    tableConfig,
    formActionsConfig,
    fieldsRendererConfig,
    setEdit,
    saveOperator,
    cancelForm,
    updateFormData,
    updateValidationErrors,
    clearValidationError,
    removeDataPropertyByKey,
    replacePropertyValueByKey
  }
}
